import React from "react"

import { ChangeEventRow, HistoryDisclosure } from "@/components/collections/ChangeEventList"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { usePlanLineage } from "@/hooks/useChangeEvents"
import { type LineageEntry, groupLineage, splitLineageByScope } from "@/lib/changeEvents"

const Feed: React.FC<{ entries: LineageEntry[]; empty: string }> = ({ entries, empty }) => {
  if (entries.length === 0) {
    return <div className="text-xs italic text-palette-jungleGreen400">{empty}</div>
  }
  return (
    <>
      {entries.map((entry, index) => (
        <ChangeEventRow
          key={entry.correlationId}
          event={entry.root}
          followOns={entry.children}
          last={index === entries.length - 1}
          showSubject
        />
      ))}
    </>
  )
}

/** A plan's change history, grouped by correlation id and split into Template and per-day lenses. */
export const PlanLineageSection: React.FC<{ plannedRouteId: string }> = ({ plannedRouteId }) => {
  const [open, setOpen] = React.useState(false)
  const [lens, setLens] = React.useState<"template" | "dates">("template")
  const { data, isLoading, error } = usePlanLineage(plannedRouteId, open)

  const { plan, instance } = React.useMemo(
    () => splitLineageByScope(groupLineage(data ?? [])),
    [data],
  )
  const total = plan.length + instance.length

  return (
    <section className="rounded-xl border border-palette-jungleGreen200 bg-palette-jungleGreen000/50">
      <HistoryDisclosure
        open={open}
        onOpenChange={setOpen}
        isLoading={open && isLoading}
        count={open && !isLoading ? total : null}
        headerClassName="px-4 py-3"
        bodyClassName="border-t border-palette-jungleGreen200 px-4 py-3.5"
      >
        {error && (
          <div className="text-xs text-palette-jungleGreen600">
            Could not load the history: {error.message}
          </div>
        )}
        {!error && !isLoading && total === 0 && (
          <div className="text-xs italic text-palette-jungleGreen400">
            No changes recorded for this route yet.
          </div>
        )}
        {!error && total > 0 && (
          <Tabs value={lens} onValueChange={(v) => setLens(v as typeof lens)}>
            <TabsList className="mb-3 h-8 w-full justify-start bg-palette-jungleGreen100 p-1">
              <TabsTrigger value="template" className="px-3 py-1 text-xs">
                Template
                {plan.length > 0 && (
                  <span className="ml-1.5 text-palette-jungleGreen500">{plan.length}</span>
                )}
              </TabsTrigger>
              <TabsTrigger value="dates" className="px-3 py-1 text-xs">
                Individual dates
                {instance.length > 0 && (
                  <span className="ml-1.5 text-palette-jungleGreen500">{instance.length}</span>
                )}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="template" className="mt-0">
              <Feed
                entries={plan}
                empty="Nothing has changed on the template itself — only individual dates."
              />
            </TabsContent>
            <TabsContent value="dates" className="mt-0">
              <Feed entries={instance} empty="No single date has been changed on its own." />
            </TabsContent>
          </Tabs>
        )}
      </HistoryDisclosure>
    </section>
  )
}

export default PlanLineageSection
